import { createTheme } from '@mui/material/styles';
import { stitchColors, stitchFonts, stitchRadius } from './tokens';

export const stitchTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: stitchColors.primary,
      contrastText: stitchColors.onPrimary,
    },
    secondary: {
      main: stitchColors.secondary,
      contrastText: stitchColors.onPrimary,
    },
    error: {
      main: stitchColors.error,
    },
    background: {
      default: stitchColors.background,
      paper: stitchColors.surfaceContainerLowest,
    },
    text: {
      primary: stitchColors.onBackground,
      secondary: stitchColors.onSurfaceVariant,
    },
    divider: stitchColors.outlineVariant,
  },
  typography: {
    fontFamily: stitchFonts.body,
    h1: {
      fontFamily: stitchFonts.headline,
      fontSize: '2.25rem',
      fontWeight: 600,
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
      color: stitchColors.onBackground,
      '@media (min-width:900px)': {
        fontSize: '3rem',
      },
    },
    h2: {
      fontFamily: stitchFonts.headline,
      fontSize: '2rem',
      fontWeight: 500,
      lineHeight: 1.3,
      color: stitchColors.onBackground,
    },
    h3: {
      fontFamily: stitchFonts.headline,
      fontSize: '1.5rem',
      fontWeight: 500,
      lineHeight: 1.4,
      color: stitchColors.onBackground,
    },
    h4: {
      fontFamily: stitchFonts.headline,
      fontSize: '1.25rem',
      fontWeight: 500,
      lineHeight: 1.35,
      color: stitchColors.onBackground,
    },
    h5: {
      fontFamily: stitchFonts.headline,
      fontSize: '1.125rem',
      fontWeight: 500,
    },
    h6: {
      fontFamily: stitchFonts.headline,
      fontSize: '1rem',
      fontWeight: 500,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
    button: {
      fontFamily: stitchFonts.body,
      fontSize: '0.875rem',
      fontWeight: 600,
      letterSpacing: '0.02em',
      textTransform: 'none',
    },
    caption: {
      fontSize: '0.75rem',
      lineHeight: 1.5,
    },
  },
  shape: {
    borderRadius: stitchRadius.md,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: stitchColors.background,
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: stitchRadius.lg,
          border: `1px solid ${stitchColors.outlineVariant}`,
          boxShadow: 'none',
          backgroundImage: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: stitchRadius.md,
          padding: '10px 20px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        containedPrimary: {
          backgroundColor: stitchColors.primary,
          '&:hover': {
            backgroundColor: stitchColors.inverseSurface,
          },
        },
        outlinedPrimary: {
          borderColor: stitchColors.primary,
          color: stitchColors.primary,
          '&:hover': {
            backgroundColor: 'rgba(4, 22, 46, 0.05)',
            borderColor: stitchColors.primary,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: stitchRadius.md,
          backgroundColor: stitchColors.surfaceContainerLowest,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: stitchColors.outlineVariant,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: stitchColors.outline,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: 2,
            borderColor: stitchColors.primary,
          },
          '&.Mui-focused': {
            boxShadow: `0 0 0 4px rgba(4, 22, 46, 0.1)`,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          fontSize: '0.875rem',
          letterSpacing: '0.02em',
          textTransform: 'uppercase',
          color: stitchColors.onSurface,
          '&.Mui-focused': {
            color: stitchColors.onSurface,
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: stitchColors.surfaceContainerLowest,
          color: stitchColors.onBackground,
          borderBottom: `1px solid ${stitchColors.navBorder}`,
          boxShadow: 'none',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: `1px solid ${stitchColors.outlineVariant}`,
          boxShadow: 'none',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
        },
      },
    },
  },
});
